import type { DashboardGateway } from '../application/dashboardGateway';
import type { AccessResult } from '../domain/access';
import type { DashboardSummary, DashboardSummaryResult } from '../domain/dashboard';
import type { SupabaseAccessGateway } from './supabaseAccessGateway';

const sessionKey = 'alpha-mls:supabase-session:v1';
const env = import.meta.env as Record<string, string | undefined>;

interface StoredSession {
  readonly accessToken: string;
  readonly expiresAt: number;
}

function readAccessToken(): string | null {
  try {
    const raw = window.localStorage.getItem(sessionKey);
    if (raw === null) return null;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('accessToken' in parsed) ||
      typeof parsed.accessToken !== 'string' ||
      !('expiresAt' in parsed) ||
      typeof parsed.expiresAt !== 'number'
    ) {
      return null;
    }

    const session: StoredSession = {
      accessToken: parsed.accessToken,
      expiresAt: parsed.expiresAt,
    };

    if (session.expiresAt <= Math.floor(Date.now() / 1000)) return null;
    return session.accessToken;
  } catch {
    return null;
  }
}

function parseCount(response: Response, table: string): number {
  const contentRange = response.headers.get('content-range');
  const total = contentRange?.split('/').at(-1);

  if (total === undefined || total === '*' || !/^\d+$/u.test(total)) {
    throw new Error(`Invalid count response for ${table}`);
  }

  return Number(total);
}

export class SupabaseDashboardGateway implements DashboardGateway {
  constructor(private readonly accessGateway: SupabaseAccessGateway) {}

  resolveCurrentIdentity(signal?: AbortSignal): Promise<AccessResult> {
    return this.accessGateway.resolveCurrentIdentity(signal);
  }

  signOut(signal?: AbortSignal): Promise<void> {
    return this.accessGateway.signOut(signal);
  }

  async loadDashboardSummary(
    signal?: AbortSignal,
  ): Promise<DashboardSummaryResult> {
    const supabaseUrl = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = readAccessToken();

    if (
      supabaseUrl === undefined ||
      publishableKey === undefined ||
      accessToken === null
    ) {
      return {
        ok: false,
        message: 'La sesión o la configuración de staging no está disponible.',
      };
    }

    const count = async (table: string): Promise<number> => {
      const response = await fetch(
        `${supabaseUrl}/rest/v1/${table}?select=id&limit=1`,
        {
          method: 'GET',
          headers: {
            apikey: publishableKey,
            Authorization: `Bearer ${accessToken}`,
            Prefer: 'count=exact',
            Accept: 'application/json',
          },
          ...(signal === undefined ? {} : { signal }),
        },
      );

      if (!response.ok) {
        throw new Error(`Unable to read ${table}`);
      }

      return parseCount(response, table);
    };

    try {
      const [
        properties,
        listings,
        agents,
        workspaces,
        agencies,
        clients,
        conversations,
      ] = await Promise.all([
        count('properties'),
        count('listings'),
        count('agents'),
        count('workspaces'),
        count('agencies'),
        count('clients'),
        count('conversations'),
      ]);

      const summary: DashboardSummary = {
        properties,
        listings,
        agents,
        workspaces,
        agencies,
        clients,
        conversations,
      };

      return { ok: true, summary };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      return {
        ok: false,
        message: 'No fue posible cargar el resumen operativo de staging.',
      };
    }
  }
}
